import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import MoleculeLoader from '../components/MoleculeLoader';
import { 
  Package, 
  Truck, 
  Clock, 
  CheckCircle2,
  Plus,
  Minus,
  Trash2,
  Send,
  ArrowLeft,
  Lock,
  Mail
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WholesalePage = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [pricingInfo, setPricingInfo] = useState(null);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });

  // Check if user has wholesale access
  useEffect(() => {
    const checkWholesaleAccess = async () => {
      try {
        const response = await fetch(`${API_URL}/api/my-pricing`, {  });
        if (response.ok) {
          const data = await response.json();
          setPricingInfo(data);
        }
      } catch (error) {
        console.error('Error checking access:', error);
      } finally {
        setCheckingAccess(false);
      }
    };
    checkWholesaleAccess();
  }, []);

  // Check if user is in wholesale tier
  const isWholesaleTier = pricingInfo?.tier?.name?.toLowerCase().includes('wholesale');

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${API_URL}/api/products`);
        if (response.ok) {
          setProducts(await response.json());
        }
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    };
    if (isWholesaleTier) {
      fetchProducts();
    }
  }, [isWholesaleTier]);

  // Pre-fill form with user info
  useEffect(() => {
    if (user && isWholesaleTier) {
      setForm(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || ''
      }));
    }
  }, [user, isWholesaleTier]);

  const addProduct = (product) => {
    const existing = selectedProducts.find(p => p.product_id === product.product_id);
    if (existing) {
      setSelectedProducts(selectedProducts.map(p => 
        p.product_id === product.product_id 
          ? { ...p, quantity: p.quantity + 10 }
          : p
      ));
    } else {
      setSelectedProducts([...selectedProducts, {
        product_id: product.product_id,
        name: product.name,
        quantity: 20
      }]);
    }
  };

  const updateQuantity = (productId, delta) => {
    setSelectedProducts(selectedProducts.map(p => 
      p.product_id === productId 
        ? { ...p, quantity: Math.max(20, p.quantity + delta) }
        : p
    ));
  };

  const removeProduct = (productId) => {
    setSelectedProducts(selectedProducts.filter(p => p.product_id !== productId));
  };

  const totalItems = selectedProducts.reduce((sum, p) => sum + p.quantity, 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedProducts.length === 0) {
      toast.error('Please select at least one product');
      return;
    }

    if (totalItems < 20) {
      toast.error('Minimum 20 items required for wholesale orders');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/wholesale/request-quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({
          ...form,
          products: selectedProducts
        })
      });

      if (!response.ok) throw new Error('Failed to submit quote request');

      setSubmitted(true);
      toast.success('Quote request submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit quote request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (checkingAccess) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <MoleculeLoader size={72} label="Checking wholesale access…" />
      </div>
    );
  }

  // Not logged in
  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50">
        <SEO 
          title="Wholesale Orders - Sign In Required"
          description="Sign in to access wholesale ordering."
        />
        <div className="max-w-2xl mx-auto px-6 py-16">
          <Card className="border-slate-100">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-6">
                <Lock className="h-8 w-8 text-slate-400" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-slate-900 mb-4">
                Sign In Required
              </h1>
              <p className="text-slate-600 mb-6">
                Please sign in to access wholesale ordering. If you don't have an account, 
                contact us to apply for wholesale access.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/auth">
                  <Button className="bg-teal-700 hover:bg-teal-800 text-white">
                    Sign In
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline">
                    Contact Us
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Not approved for wholesale
  if (!isWholesaleTier) {
    return (
      <div className="min-h-screen bg-slate-50">
        <SEO 
          title="Wholesale Access Required"
          description="Apply for wholesale access to place bulk orders."
        />
        <div className="max-w-2xl mx-auto px-6 py-16">
          <Card className="border-slate-100">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
                <Package className="h-8 w-8 text-amber-600" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-slate-900 mb-4">
                Wholesale Access Required
              </h1>
              <p className="text-slate-600 mb-2">
                Hi <strong>{user.name}</strong>, wholesale ordering is available to approved accounts only.
              </p>
              <p className="text-slate-600 mb-6">
                To apply for wholesale access, please contact us with your business information 
                and we'll review your application.
              </p>
              
              <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left">
                <h3 className="font-semibold text-slate-900 mb-2">Wholesale Benefits:</h3>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-teal-600" />
                    20% discount on all products
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-teal-600" />
                    Minimum order: 20 vials
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-teal-600" />
                    Free shipping on all orders
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-teal-600" />
                    Dedicated account support
                  </li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/contact">
                  <Button className="bg-teal-700 hover:bg-teal-800 text-white">
                    <Mail className="h-4 w-4 mr-2" />
                    Apply for Wholesale Access
                  </Button>
                </Link>
                <Link to="/products">
                  <Button variant="outline">
                    Browse Products
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50">
        <SEO 
          title="Wholesale Quote Submitted"
          description="Thank you for your wholesale inquiry."
        />
        <div className="max-w-2xl mx-auto px-6 py-16">
          <Card className="border-slate-100">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-slate-900 mb-4">
                Quote Request Submitted!
              </h1>
              <p className="text-slate-600 mb-6">
                Thank you for your wholesale inquiry. Our team will review your request and get back to you within 1-2 business days with pricing and availability.
              </p>
              <div className="bg-teal-50 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-center gap-2 text-teal-700">
                  <Clock className="h-5 w-5" />
                  <span className="font-medium">Typical order turnaround: 2 weeks</span>
                </div>
              </div>
              <Link to="/products">
                <Button className="bg-teal-700 hover:bg-teal-800 text-white">
                  Continue Shopping
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO 
        title="Wholesale Orders"
        description="Request wholesale pricing for bulk peptide orders. Minimum 20 vials required. Orders typically ship within 2 weeks."
        keywords="wholesale peptides, bulk order, research peptides wholesale"
      />
      
      {/* Header */}
      <div className="bg-slate-900 py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Link to="/products" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-4">
            <ArrowLeft className="h-4 w-4" />
            Back to Products
          </Link>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
            Wholesale Orders
          </h1>
          <p className="text-slate-300 max-w-2xl">
            Request custom pricing for bulk orders. Minimum 20 vials required across all products.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        {/* Benefits */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <Card className="border-slate-100">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-4">
                <Package className="h-6 w-6 text-teal-600" />
              </div>
              <h3 className="font-heading font-semibold text-slate-900 mb-2">Bulk Pricing</h3>
              <p className="text-sm text-slate-500">Up to 20% off on wholesale orders of 20+ vials</p>
            </CardContent>
          </Card>
          <Card className="border-slate-100">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-4">
                <Clock className="h-6 w-6 text-teal-600" />
              </div>
              <h3 className="font-heading font-semibold text-slate-900 mb-2">2 Week Turnaround</h3>
              <p className="text-sm text-slate-500">Orders are prepared and shipped within 2 weeks</p>
            </CardContent>
          </Card>
          <Card className="border-slate-100">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-4">
                <Truck className="h-6 w-6 text-teal-600" />
              </div>
              <h3 className="font-heading font-semibold text-slate-900 mb-2">Free Shipping</h3>
              <p className="text-sm text-slate-500">Complimentary shipping on all wholesale orders</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Product Selection */}
          <div>
            <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">Select Products</h2>
            <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
              {products.map((product) => (
                <Card key={product.product_id} className="border-slate-100">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <img 
                          src={product.image_url} 
                          alt={product.name}
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                        <div>
                          <h3 className="font-medium text-slate-900">{product.name}</h3>
                          <p className="text-sm text-slate-500">{product.category}</p>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => addProduct(product)}
                        className="border-teal-200 text-teal-700 hover:bg-teal-50"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Quote Form */}
          <div>
            <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">Request Quote</h2>
            
            {/* Selected Products */}
            {selectedProducts.length > 0 && (
              <Card className="border-slate-100 mb-6">
                <CardContent className="p-4">
                  <h3 className="text-sm font-medium text-slate-700 mb-3">Selected Products</h3>
                  <div className="space-y-3">
                    {selectedProducts.map((product) => (
                      <div key={product.product_id} className="flex items-center justify-between">
                        <span className="text-sm text-slate-900">{product.name}</span>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateQuantity(product.product_id, -10)}
                            className="p-1 text-slate-400 hover:text-slate-600"
                          >
                            <Minus className="h-4 w-4" />
                          </button>
                          <span className="w-12 text-center font-medium">{product.quantity}</span>
                          <button
                            onClick={() => updateQuantity(product.product_id, 10)}
                            className="p-1 text-slate-400 hover:text-slate-600"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => removeProduct(product.product_id)}
                            className="p-1 text-slate-400 hover:text-red-500 ml-2"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-3 border-t border-slate-100 flex justify-between items-center">
                    <span className="text-sm font-medium text-slate-700">Total Items</span>
                    <Badge className={`${totalItems >= 20 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                      {totalItems} {totalItems >= 20 ? '✓' : `(min 20)`}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            )}

            <form onSubmit={handleSubmit}>
              <Card className="border-slate-100">
                <CardContent className="p-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Name *</Label>
                      <Input
                        placeholder="Your name"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        placeholder="email@company.com"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Company</Label>
                      <Input
                        placeholder="Company name"
                        value={form.company}
                        onChange={(e) => setForm({ ...form, company: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone</Label>
                      <Input
                        placeholder="+1 (555) 123-4567"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Additional Notes</Label>
                    <Textarea
                      placeholder="Any specific requirements or questions..."
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      rows={3}
                    />
                  </div>

                  <div className="bg-amber-50 rounded-lg p-4 text-sm text-amber-700">
                    <strong>Note:</strong> Wholesale orders require full payment in advance. 
                    Orders typically ship within 2 weeks of payment confirmation.
                  </div>

                  <Button
                    type="submit"
                    disabled={loading || selectedProducts.length === 0 || totalItems < 20}
                    className="w-full bg-teal-700 hover:bg-teal-800 text-white h-12"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                        Submitting...
                      </span>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Submit Quote Request
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WholesalePage;
